import { h } from 'preact'
import clsx from 'clsx'

interface PromolineData {
  title: string
  body?: string
  cta?: {
    text?: string
    url?: string
  }
  text_colour: string
  background_colour?: string
  priority: number
}

interface PromolineProps {
  promoline: PromolineData
  promolineSize?: 'small' | 'medium' | 'large'
  title?: string
  body?: string
  cta_text?: string
  cta_url?: string
  text_colour?: string
  bg_colour?: string
}

export const Promoline = ({
  promoline,
  promolineSize = 'small',
  title: titleOverride,
  body: bodyOverride,
  cta_text: ctaTextOverride,
  cta_url: ctaUrlOverride,
  text_colour: textColourOverride,
  bg_colour: bgColourOverride,
  className
}: PromolineProps & { className?: string }) => {
  if (!promoline || !promoline.title) return null

  // Apply overrides with defaults from promoline data
  const title = titleOverride ?? promoline.title
  const body = bodyOverride ?? promoline.body
  const ctaText = ctaTextOverride ?? promoline.cta?.text
  const ctaUrl = ctaUrlOverride ?? promoline.cta?.url
  const textColour = textColourOverride ?? promoline.text_colour
  const bgColour = bgColourOverride ?? promoline.background_colour

  // Size-based classes
  const getSizeClasses = () => {
    switch (promolineSize) {
      case 'small':
        return {
          container: 'w-fit',
          containerBg: 'px-0.5 py-0.188'
        }
      case 'medium':
        return {
          container: 'w-full',
          containerBg: 'px-0.75 py-0.188'
        }
      case 'large':
        return {
          container: 'w-full',
          containerBg: 'p-1 lg:py-0.5'
        }
      default:
        return {
          container: 'w-fit',
          containerBg: 'px-0.5 py-0.188'
        }
    }
  }

  const sizeClasses = getSizeClasses()
  const Component = ctaUrl ? 'a' : 'div'

  const componentProps = ctaUrl ? {
    href: ctaUrl,
    'aria-label': title
  } : {}

  return (
    <Component
      {...componentProps}
      className={clsx(
        'shrink-0 flex flex-wrap items-center justify-between gap-x-0.5 gap-y-0.25 h-fit',
        sizeClasses.container,
        'text-[--text-colour] bg-[--bg-colour]',
        {
          [`${sizeClasses.containerBg} rounded-promoline overflow-hidden`]: bgColour,
          'link-fade': ctaUrl
        },
        className
      )}
      style={{
        '--text-colour': textColour,
        '--bg-colour': bgColour || 'transparent'
      } as React.CSSProperties}
    >
      <span className={clsx(
        'shrink-0',
        promolineSize === 'large' ? 'paragraph-s font-bold' : 'paragraph-xs font-medium'
      )}>
        {title}
      </span>
      <div className={clsx('shrink-0 flex items-center justify-end gap-x-0.5 empty:hidden', body && promolineSize === 'large' ? 'grow' : '')}>
        {body && promolineSize === 'large' && (
          <span className="grow paragraph-xs">
            {body}
          </span>
        )}
        {ctaText && promolineSize !== 'small' && (
          <span className="shrink-0 paragraph-xs text-line">
            {ctaText}
          </span>
        )}
      </div>
    </Component>
  )
}
